"use client";
import { WhatsAppPhone, type WhatsAppChatHeader } from "@/components/marketing";
import { useId } from "react";
import { useOccasionPlanner } from "./hooks";

interface SendMode {
  when: string;
  note: string;
}

export interface OccasionPlannerData {
  ariaLabel: string;
  occasionLegend: string;
  // `message` uses {reward}; `isCampaign` = sent manually, not automatically.
  occasions: { key: string; label: string; message: string; isCampaign?: boolean }[];
  rewardLegend: string;
  // `inMessage` is how the reward reads inside the message.
  rewards: { key: string; label: string; inMessage: string }[];
  fine: string;
  chat: WhatsAppChatHeader;
  time: string;
  whenLabel: string;
  automatic: SendMode;
  campaign: SendMode;
}

const legendClass = "mb-1.5 text-[14px] font-bold text-ink-muted";
const optionClass = "flex cursor-pointer items-center gap-2 text-[15.5px]";

// "Plan an occasion message" - the preview's `occasion_planner`. Pick an
// occasion and a reward; the WhatsApp message and how it is sent update.
const OccasionPlanner = ({
  ariaLabel,
  occasionLegend,
  occasions,
  rewardLegend,
  rewards,
  fine,
  chat,
  time,
  whenLabel,
  automatic,
  campaign,
}: OccasionPlannerData) => {
  const { occasion, reward, setOccasion, setReward } = useOccasionPlanner(
    occasions[0].key,
    rewards[0].key,
  );
  const name = useId();
  const picked = occasions.find((item) => item.key === occasion) ?? occasions[0];
  const pickedReward = rewards.find((item) => item.key === reward) ?? rewards[0];
  const mode = picked.isCampaign ? campaign : automatic;

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0">
      <div className="grid items-center gap-7 xl:grid-cols-[minmax(0,1fr)_272px_minmax(0,1fr)]">
        <div className="grid gap-[18px] rounded-3xl bg-white p-[22px]">
          <fieldset className="grid gap-2">
            <legend className={legendClass}>{occasionLegend}</legend>
            {occasions.map((item) => (
              <label key={item.key} className={optionClass}>
                <input
                  type="radio"
                  name={`${name}-occasion`}
                  value={item.key}
                  checked={item.key === occasion}
                  onChange={() => setOccasion(item.key)}
                  className="size-4 accent-primary"
                />
                {item.label}
              </label>
            ))}
          </fieldset>
          <fieldset className="grid gap-2">
            <legend className={legendClass}>{rewardLegend}</legend>
            {rewards.map((item) => (
              <label key={item.key} className={optionClass}>
                <input
                  type="radio"
                  name={`${name}-reward`}
                  value={item.key}
                  checked={item.key === reward}
                  onChange={() => setReward(item.key)}
                  className="size-4 accent-primary"
                />
                {item.label}
              </label>
            ))}
          </fieldset>
          <p className="text-[14px] leading-relaxed text-ink-muted">{fine}</p>
        </div>

        <div className="justify-self-center">
          <WhatsAppPhone
            chat={chat}
            messages={[
              { text: picked.message.replace("{reward}", pickedReward.inMessage), time },
            ]}
          />
        </div>

        <div aria-live="polite" className="grid content-center gap-1.5">
          <span className="text-[13px] font-bold text-ink-muted">{whenLabel}</span>
          <b className="text-[1.25rem] leading-tight font-bold">{mode.when}</b>
          <p className="text-base leading-relaxed text-ink-muted">{mode.note}</p>
        </div>
      </div>
    </figure>
  );
};

export default OccasionPlanner;
